import { LearnNotation, cellsPerBeat, grooveToAbc, parseUrl, totalCells, type LearnTimeline } from "@wmf/groove";
import { useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "./ui/Dialog.js";
import { ApiError } from "./api.js";
import { tapLearnTempo, type LearnTap } from "./grooveLearnTempo.js";
import "./grooveLearnMode.css";

/** The same learning view serves library grooves and private/School snapshots. */
export interface GrooveLearnTempoSave {
  onSaveTempo?: (tempo: number) => void | Promise<void>;
  tempoSaveLabel?: string;
  tempoSaveDescription?: string;
  tempoSavedMessage?: string;
  tempoValueLabel?: string;
}

type GrooveLearnModeProps = GrooveLearnTempoSave & {
  query: string;
  title: string;
  onOpen?: () => void;
  disabled?: boolean;
  buttonAriaLabel?: string;
};

export function GrooveLearnMode(props: GrooveLearnModeProps) {
  const { title, onOpen, disabled = false, buttonAriaLabel } = props;
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className="quiet groove-learn-button" disabled={disabled}
      aria-haspopup="dialog" aria-label={buttonAriaLabel} onClick={() => { onOpen?.(); setOpen(true); }}>
      Learn mode
    </button>
    {/* Parse saved queries only when opened, rather than engraving every library card. */}
    {open && createPortal(<LearnSession
      query={props.query} onSaveTempo={props.onSaveTempo} tempoSaveLabel={props.tempoSaveLabel}
      tempoSaveDescription={props.tempoSaveDescription} tempoSavedMessage={props.tempoSavedMessage}
      tempoValueLabel={props.tempoValueLabel}
      title={title} onClose={() => setOpen(false)} />, document.body)}
  </>;
}

function LearnSession({ query: initialQuery, title: initialTitle, onClose, onSaveTempo: initialSaveTempo,
  tempoSaveLabel = "Save practice tempo", tempoSaveDescription, tempoSavedMessage = "Practice tempo saved.", tempoValueLabel = "Saved tempo" }:
  GrooveLearnTempoSave & { query: string; title: string; onClose: () => void }) {
  // A tempo save refreshes the parent snapshot. Keep this learning session and
  // its arrow in place, rather than remounting it halfway through the rhythm.
  const [source] = useState(() => parseUrl(initialQuery));
  const [abc] = useState(() => grooveToAbc(source));
  const [title] = useState(initialTitle);
  const [onSaveTempo] = useState(() => initialSaveTempo);
  const [step, setStep] = useState(0);
  const [count, setCount] = useState(0);
  const [timeline, setTimeline] = useState<LearnTimeline>({ positions: [], duration: 0 });
  const [bpm, setBpm] = useState<number | null>(null);
  const [savedBpm, setSavedBpm] = useState(source.tempo);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const tap = useRef<LearnTap | null>(null);
  const savePending = useRef(false);
  const score = useRef<HTMLDivElement>(null);
  function resetTempo() {
    tap.current = null;
    setBpm(null);
    setSavedMessage(null);
    setProblem(null);
  }
  function tapNext() {
    if (count === 0 || saving) return;
    const next = step === count - 1 ? 0 : step + 1;
    const result = tapLearnTempo(tap.current, performance.now(), next, timeline.positions,
      timeline.duration, totalCells(source) / cellsPerBeat(source.timeSig, source.div));
    tap.current = result.tap;
    setBpm(result.bpm);
    setSavedMessage(null);
    setProblem(null);
    setStep(next);
    score.current?.focus({ preventScroll: true });
  }
  function move(direction: number) {
    resetTempo();
    setStep(previous => Math.max(0, Math.min(Math.max(0, count - 1), previous + direction)));
    // A boundary button becomes disabled after activation. Return focus to
    // the score so Space still steps through notes instead of reaching the
    // page's background playback shortcut.
    score.current?.focus({ preventScroll: true });
  }
  function handleKey(event: KeyboardEvent) {
    const space = event.code === "Space" || event.key === " ";
    if (!space && event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      // Leave browser shortcuts intact, without leaking Space to background
      // groove playback while this learning view is open.
      event.stopPropagation();
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest("input, select, textarea, [contenteditable='true']")) return;
    // Space on a button keeps its normal accessible activation behaviour.
    if (space && target instanceof Element && target.closest("button")) return;
    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat && !saving) {
      if (space) tapNext();
      else move(event.key === "ArrowLeft" ? -1 : 1);
    }
  }
  async function saveTempo() {
    if (!onSaveTempo || bpm === null || bpm < 20 || bpm > 300 || savePending.current) return;
    savePending.current = true;
    setSaving(true);
    setProblem(null);
    setSavedMessage(null);
    try {
      await onSaveTempo(bpm);
      setSavedBpm(bpm);
      setSavedMessage(tempoSavedMessage);
      tap.current = null;
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Could not save the practice tempo. Please try again.";
      setProblem(cause instanceof ApiError && cause.status === 409 && cause.code !== "school_groove_practice_ownership_conflict"
        ? `${message} Close Learn mode and reload the page before trying again.` : message);
    } finally {
      savePending.current = false;
      setSaving(false);
      score.current?.focus({ preventScroll: true });
    }
  }
  return <div onKeyDownCapture={handleKey} className="groove-learn-session">
    <Dialog title="Learn mode" eyebrow={title.trim() || "Your groove"} onClose={onClose}
      description="Follow the arrow, one note at a time. Drums played together count as one step."
      size="wide" className="groove-learn-dialog" initialFocusRef={score} closeLabel="Done" closeButtonText="Done" busy={saving}>
      <div className="groove-learn-help">
        <span><kbd>Space</kbd> / <kbd>→</kbd> Next note</span>
        <span><kbd>←</kbd> Previous note</span>
      </div>
      <div ref={score} className="groove-learn-score" tabIndex={0} role="region"
        aria-label="Learning score. Space or right arrow for next note, left arrow for previous note.">
        <LearnNotation abc={abc} step={step} onStepCount={setCount} onTimeline={setTimeline} />
      </div>
      <section className="groove-learn-tempo" aria-label="Practice tempo">
        <div>
          <span className="small">Tap tempo</span>
          <output aria-label="Tapped tempo" className="groove-learn-bpm">{bpm === null ? "—" : bpm} <small>BPM</small></output>
          <p className="small muted">{bpm === null ? "Press Space in rhythm. Tap twice to see the tempo." : "Follows the written rhythm, including rests. Average of recent taps."}</p>
          <p className="small">{tempoValueLabel}: <strong>{savedBpm} BPM</strong></p>
        </div>
        <div className="groove-learn-tempo-actions">
          <button type="button" onClick={tapNext} disabled={count === 0 || saving}>Tap / next note</button>
          <button type="button" className="quiet" onClick={() => { resetTempo(); score.current?.focus({ preventScroll: true }); }} disabled={saving}>Reset taps</button>
          {onSaveTempo && <button type="button" onClick={() => void saveTempo()}
            disabled={saving || bpm === null || bpm < 20 || bpm > 300 || bpm === savedBpm}>{saving ? "Saving…" : tempoSaveLabel}</button>}
        </div>
        {onSaveTempo && tempoSaveDescription && <p className="small muted groove-learn-tempo-message">{tempoSaveDescription}</p>}
        {bpm !== null && (bpm < 20 || bpm > 300) && <p className="small groove-learn-tempo-message">Tap a tempo from 20 to 300 BPM to save it.</p>}
        {savedMessage && <p role="status" className="groove-learn-tempo-message">{savedMessage}</p>}
        {problem && <p role="alert" className="error groove-learn-tempo-message">{problem}</p>}
      </section>
      <div className="groove-learn-controls">
        <p role="status" aria-live="polite" aria-atomic="true">
          {count > 0 ? `Step ${step + 1} of ${count}${step === count - 1 ? " · Last note" : ""}` : "There are no notes to step through yet."}
        </p>
        <div className="groove-learn-navigation">
          <button type="button" className="quiet" disabled={saving || count === 0 || step === 0} onClick={() => move(-1)}>← Previous</button>
          <button type="button" disabled={saving || count === 0 || step === count - 1} onClick={() => move(1)}>Next →</button>
          <button type="button" className="quiet" disabled={saving || count === 0} onClick={() => { resetTempo(); setStep(0); score.current?.focus({ preventScroll: true }); }}>Start again</button>
        </div>
      </div>
    </Dialog>
  </div>;
}
